<script setup lang="ts">
  import axios from 'axios';
  import RestaurantsService from '@/api/services/restaurants.service';
  import { PosAdminGrantsService } from '@/api/services/pos-admin-grants.service';
  import { useModal } from '@/composition/use-modal';
  import { useUi } from '@/composition/use-ui';
  import { PermissionType } from '@/enums/PermissionType';
  import { IAuthData } from '@/interfaces/IAuthData';
  import { IMember, MemberRole } from '@/interfaces/IMember';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { mapGrantErrorToI18nKey } from '@/utils/pos-admin-grant.utils';
  import { computed, getCurrentInstance, onMounted, ref, watch } from 'vue';

  const emit = defineEmits(['close']);
  const modals = useModal();
  const ui = useUi();
  const restaurantStore = useRestaurantStore();

  // vue-i18n in script (Vue 2.7): translate through the component proxy.
  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string): string {
    return String(proxy.$t(key));
  }

  const props = defineProps<{
    requiredPermission: PermissionType;
    /** Programmatic audit description included in the emitted IAuthData. */
    reason?: string;
  }>();

  const authTabs = [
    { value: 'pin', labelKey: 'adminAuth.tabPin' },
    { value: 'token', labelKey: 'adminAuth.tabToken' },
  ];

  const selectedAuthIndex = ref(0);
  const isPinTab = computed(
    () => authTabs[selectedAuthIndex.value].value === 'pin'
  );

  const members = ref<IMember[] | null>(null);
  const membersLoadFailed = ref(false);

  // A load failure only breaks the PIN tab; the token flow doesn't need the
  // members list, so the modal stays open with an inline error + retry.
  async function loadMembers() {
    membersLoadFailed.value = false;
    try {
      members.value = await RestaurantsService.getMembers(
        restaurantStore.restaurant?.id
      );
    } catch (error) {
      ui.serverError(error, i18nT('adminAuth.loadUsersError'));
      membersLoadFailed.value = true;
    }
  }

  onMounted(loadMembers);

  const availableMembers = computed(() => {
    if (!members.value) return null;

    return members.value.filter(
      (m) =>
        m.type === MemberRole.Admin ||
        m.type === MemberRole.Owner ||
        (m.permissions & props.requiredPermission) === props.requiredPermission
    );
  });

  // PIN tab state
  const selectedMember = ref<IMember | null>(null);
  const pinValue = ref('');

  function selectMember(member: IMember) {
    if (member.hasPin === false) return;
    selectedMember.value = member;
    pinValue.value = '';
  }

  function changeUser() {
    selectedMember.value = null;
    pinValue.value = '';
  }

  // Token tab state
  const tokenCode = ref('');
  const isVerifyingToken = ref(false);
  const tokenErrorKey = ref<string | null>(null);

  watch(selectedAuthIndex, () => {
    pinValue.value = '';
    tokenCode.value = '';
    tokenErrorKey.value = null;
  });

  const canConfirm = computed(() => {
    if (isPinTab.value) {
      return !!selectedMember.value && pinValue.value.length > 0;
    }
    return /^[0-9]{6}$/.test(tokenCode.value.trim()) && !isVerifyingToken.value;
  });

  const showConfirm = computed(() => !isPinTab.value || !!selectedMember.value);

  function submitPin() {
    if (!selectedMember.value || !pinValue.value) return;

    modals.dialogSubmit('admin-pin', <IAuthData>{
      type: 'pin',
      userId: selectedMember.value.userId,
      value: pinValue.value,
      ...(props.reason ? { reason: props.reason } : {}),
    });
  }

  function extractServerMessage(error: unknown): string | undefined {
    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.message;
      if (typeof message === 'string') return message;
    }
    return undefined;
  }

  async function submitToken() {
    const restaurantId = restaurantStore.restaurant?.id;
    const code = tokenCode.value.trim();
    if (!restaurantId || isVerifyingToken.value || !/^[0-9]{6}$/.test(code)) {
      return;
    }

    isVerifyingToken.value = true;
    tokenErrorKey.value = null;

    try {
      const res = await PosAdminGrantsService.verify(
        restaurantId,
        code,
        props.requiredPermission
      );
      // dialogSubmit unmounts this modal: never mutate local state after it
      // (renderer-safety rule), so isVerifyingToken is only reset on failure.
      modals.dialogSubmit('admin-pin', <IAuthData>{
        type: 'token',
        userId: res.authorizedByUserId,
        value: res.grantId,
        ...(props.reason ? { reason: props.reason } : {}),
      });
    } catch (error) {
      tokenErrorKey.value = mapGrantErrorToI18nKey(extractServerMessage(error));
      isVerifyingToken.value = false;
    }
  }

  function confirm() {
    if (isPinTab.value) {
      submitPin();
    } else {
      void submitToken();
    }
  }
</script>

<template>
  <v-card class="pin-modal tw-flex tw-flex-col" elevation="0">
    <v-card-title>
      <span>{{ $t('adminAuth.title') }}</span>
    </v-card-title>
    <v-card-text class="card-text">
      <div class="modal-body">
        <v-tabs v-model="selectedAuthIndex">
          <v-tab v-for="tab in authTabs" :key="tab.value">
            {{ $t(tab.labelKey) }}
          </v-tab>
        </v-tabs>
        <v-tabs-items v-model="selectedAuthIndex">
          <v-tab-item class="tab-content">
            <template v-if="!selectedMember">
              <h3>{{ $t('adminAuth.selectUser') }}</h3>
              <div class="users">
                <template v-if="membersLoadFailed">
                  <span>{{ $t('adminAuth.loadUsersError') }}</span>
                  <v-btn text small color="primary" @click="loadMembers">
                    <v-icon left small>mdi-refresh</v-icon>
                    {{ $t('adminAuth.retry') }}
                  </v-btn>
                </template>
                <template v-else-if="!availableMembers">
                  <v-skeleton-loader type="list-item-avatar-two-line" />
                </template>
                <template v-else-if="availableMembers.length === 0">
                  <span>{{ $t('adminAuth.noUsers') }}</span>
                </template>
                <template v-else>
                  <v-btn
                    v-for="member in availableMembers"
                    :key="member.userId"
                    class="user-btn"
                    block
                    color="white"
                    :disabled="member.hasPin === false"
                    @click="selectMember(member)"
                  >
                    <span class="user-btn-name">{{ member.user?.name }}</span>
                    <span v-if="member.hasPin === false" class="user-btn-note">
                      {{ $t('adminAuth.noPin') }}
                    </span>
                  </v-btn>
                </template>
              </div>
            </template>
            <template v-else>
              <v-btn text small class="change-user-btn" @click="changeUser">
                <v-icon left small>mdi-arrow-left</v-icon>
                {{ $t('adminAuth.changeUser') }}
              </v-btn>
              <div class="selected-user">{{ selectedMember.user?.name }}</div>
              <span>{{ $t('adminAuth.pinInstruction') }}</span>
              <v-text-field
                v-model="pinValue"
                outlined
                dense
                :label="$t('adminAuth.pinLabel')"
                :placeholder="$t('adminAuth.pinPlaceholder')"
                type="password"
                autofocus
                autocomplete="null"
              ></v-text-field>
            </template>
          </v-tab-item>
          <v-tab-item class="tab-content">
            <span>{{ $t('adminAuth.tokenInstruction') }}</span>
            <v-text-field
              v-model="tokenCode"
              outlined
              dense
              :label="$t('adminAuth.tokenLabel')"
              :placeholder="$t('adminAuth.tokenPlaceholder')"
              :hint="$t('adminAuth.tokenHint')"
              persistent-hint
              inputmode="numeric"
              maxlength="6"
              autocomplete="null"
            ></v-text-field>
            <v-alert
              v-if="tokenErrorKey"
              type="error"
              dense
              outlined
              class="token-error"
            >
              {{ $t(tokenErrorKey) }}
            </v-alert>
          </v-tab-item>
        </v-tabs-items>
      </div>
    </v-card-text>
    <v-card-actions class="justify-end">
      <v-btn text @click="emit('close')">{{ $t('cancel') }}</v-btn>
      <v-btn
        v-if="showConfirm"
        color="primary"
        :disabled="!canConfirm"
        @click="confirm"
        >{{ $t('confirm') }}</v-btn
      >
    </v-card-actions>
  </v-card>
</template>

<style scoped lang="scss">
  .pin-modal {
    @apply tw-bg-transparent tw-border-none;

    .card-text {
      @apply tw-flex-1 tw-min-h-1 tw-overflow-y-auto;
    }
  }

  .modal-body {
    @apply tw-space-y-2;

    .tab-content {
      @apply tw-space-y-2 tw-pt-2;
    }

    .users {
      @apply tw-space-y-2;

      .user-btn {
        @apply tw-h-14;

        .user-btn-name {
          @apply tw-font-medium;
        }

        .user-btn-note {
          @apply tw-ml-2 tw-text-xs tw-uppercase tw-opacity-60;
        }
      }
    }

    .change-user-btn {
      @apply tw-mb-1;
    }

    .selected-user {
      @apply tw-font-semibold;
    }

    .token-error {
      @apply tw-mt-2 tw-mb-0;
    }
  }
</style>
